/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';

import { DATE_BASIS_OPTIONS, OUTCOME_COLORS } from '../constants';
import {
   DASH,
   formatCurrency,
   formatNumber,
   formatPercent,
   pluralTitulos,
} from '../utils/format';
import {
   CardHead,
   CardSubtitle,
   CardTitle,
   MeterFill,
   MeterTrack,
   MetricBlock,
   MetricDenominator,
   MetricGrid,
   MetricLabel,
   MetricUnit,
   MetricValue,
   MetricValueRow,
   OutcomeBlock,
   OutcomeHeadline,
   OutcomeKey,
   OutcomeLegend,
   OutcomeSegment,
   OutcomeTrack,
   Pill,
} from '../styles';
import { summaryShape } from '../utils/shapes';

// ---------------------------------------------------------------------------
// Dobra 2 · Qualidade do crédito.
//
// Quatro métricas do `summary`, todas sob o MESMO denominador: os títulos do
// recorte cujo vencimento já ocorreu. Adimplência e recuperação dividem por ele,
// e por isso `pagos_ate_vencimento + pagos_apos_vencimento` pode somar MENOS que
// o denominador — a diferença são os que venceram e seguem em aberto. Sem o
// denominador impresso, a taxa não é auditável; é por isso que ele aparece em
// texto embaixo de cada bloco, e não só no subtítulo.
//
// ⚠️ null ≠ 0: sem denominador, a taxa vem `null`. Nesse caso o valor é "—" e a
// BARRA NÃO É RENDERIZADA. Uma barra vazia leria como "0%", que é uma afirmação
// sobre a carteira — e nós não temos essa informação.
// ---------------------------------------------------------------------------

// Desfecho dos títulos já vencidos: as duas taxas dividem o MESMO denominador,
// então somá-las é legítimo — e é a soma que conta a história real da operação.
function OutcomeSummary({ summary }) {
   const denominator = summary?.adimplencia_em_dia?.vencidos_no_periodo ?? 0;
   if (!denominator) return null;

   const onTime = summary?.adimplencia_em_dia?.pagos_ate_vencimento ?? 0;
   const recovered = summary?.recuperacao?.pagos_apos_vencimento ?? 0;
   // O resto não é "perdido": é o que venceu e ainda está em aberto.
   const stillOpen = Math.max(0, denominator - onTime - recovered);

   const share = (count) => (count / denominator) * 100;
   const paidShare = share(onTime + recovered);

   const segments = [
      {
         key: 'onTime',
         label: 'Pago em dia',
         count: onTime,
         color: OUTCOME_COLORS.paidOnTime,
      },
      {
         key: 'recovered',
         label: 'Pago em atraso',
         count: recovered,
         color: OUTCOME_COLORS.recovered,
      },
      {
         key: 'stillOpen',
         label: 'Segue em aberto',
         count: stillOpen,
         color: OUTCOME_COLORS.stillOpen,
      },
   ];

   return (
      <OutcomeBlock>
         <OutcomeHeadline>
            <strong>{formatPercent(paidShare, 1)}</strong> de tudo que venceu no
            período acabou pago — somando quem pagou em dia e quem pagou depois.
         </OutcomeHeadline>

         <OutcomeTrack
            role="img"
            aria-label={`Desfecho dos ${formatNumber(denominator)} títulos já vencidos: ${segments
               .map(
                  (segment) =>
                     `${segment.label}, ${formatPercent(share(segment.count), 1)}`,
               )
               .join('; ')}`}
         >
            {segments
               .filter((segment) => segment.count > 0)
               .map((segment) => (
                  <OutcomeSegment
                     key={segment.key}
                     style={{ flexGrow: share(segment.count), flexBasis: 0 }}
                     $color={segment.color}
                     title={`${segment.label} · ${pluralTitulos(segment.count)} · ${formatPercent(share(segment.count), 1)}`}
                  />
               ))}
         </OutcomeTrack>

         <OutcomeLegend>
            {segments.map((segment) => (
               <OutcomeKey key={segment.key} $color={segment.color}>
                  {segment.label} · {formatPercent(share(segment.count), 1)} (
                  {formatNumber(segment.count)})
               </OutcomeKey>
            ))}
         </OutcomeLegend>
      </OutcomeBlock>
   );
}

OutcomeSummary.propTypes = { summary: summaryShape.isRequired };

function Meter({ pct, color }) {
   if (pct === null || pct === undefined) return null;
   const width = Math.max(0, Math.min(100, pct));
   return (
      <MeterTrack>
         <MeterFill style={{ width: `${width}%` }} $color={color} />
      </MeterTrack>
   );
}

Meter.propTypes = {
   pct: PropTypes.number,
   color: PropTypes.string.isRequired,
};

export default function CreditQuality({ summary, dateBasis }) {
   const adimplencia = summary?.adimplencia_em_dia;
   const recuperacao = summary?.recuperacao;
   const denominator = adimplencia?.vencidos_no_periodo ?? 0;

   const basisOption =
      DATE_BASIS_OPTIONS.find((option) => option.key === dateBasis) ??
      DATE_BASIS_OPTIONS[0];

   const rate = adimplencia?.taxa_pct ?? null;

   return (
      <>
         <CardHead>
            <div>
               <CardTitle as="h3">Qualidade do crédito</CardTitle>
               <CardSubtitle>
                  Denominador único: adimplência e recuperação dividem pelos{' '}
                  {formatNumber(denominator)} títulos já vencidos no período.
               </CardSubtitle>
            </div>
            <Pill>{basisOption.pill}</Pill>
         </CardHead>

         <OutcomeSummary summary={summary} />

         <MetricGrid>
            <MetricBlock>
               <MetricLabel>Adimplência em dia</MetricLabel>
               <MetricValueRow>
                  <MetricValue>{formatPercent(rate)}</MetricValue>
               </MetricValueRow>
               <Meter pct={rate} color={OUTCOME_COLORS.paidOnTime} />
               <MetricDenominator>
                  Pagos até o vencimento:{' '}
                  {formatNumber(adimplencia?.pagos_ate_vencimento)} de{' '}
                  {formatNumber(denominator)} títulos já vencidos
               </MetricDenominator>
            </MetricBlock>

            <MetricBlock>
               <MetricLabel>Taxa de recuperação</MetricLabel>
               <MetricValueRow>
                  <MetricValue>
                     {formatPercent(recuperacao?.taxa_pct ?? null)}
                  </MetricValue>
               </MetricValueRow>
               <Meter
                  pct={recuperacao?.taxa_pct ?? null}
                  color={OUTCOME_COLORS.recovered}
               />
               <MetricDenominator>
                  Pagos após o vencimento:{' '}
                  {recuperacao?.pagos_apos_vencimento
                     ? `${pluralTitulos(recuperacao.pagos_apos_vencimento)} · ${formatCurrency(recuperacao.valor)}`
                     : '0 títulos'}
               </MetricDenominator>
            </MetricBlock>

            <MetricBlock>
               <MetricLabel>Atraso médio</MetricLabel>
               <MetricValueRow>
                  <MetricValue>
                     {summary?.atraso_medio_dias === null ||
                     summary?.atraso_medio_dias === undefined
                        ? DASH
                        : formatNumber(summary.atraso_medio_dias, 1)}
                  </MetricValue>
                  {summary?.atraso_medio_dias !== null &&
                  summary?.atraso_medio_dias !== undefined ? (
                     <MetricUnit>dias</MetricUnit>
                  ) : null}
               </MetricValueRow>
               <MetricDenominator>
                  Média apenas dos títulos pagos com atraso.
               </MetricDenominator>
            </MetricBlock>

            <MetricBlock>
               <MetricLabel>DSO</MetricLabel>
               <MetricValueRow>
                  <MetricValue>
                     {summary?.dso_dias === null ||
                     summary?.dso_dias === undefined
                        ? DASH
                        : formatNumber(summary.dso_dias, 1)}
                  </MetricValue>
                  {summary?.dso_dias !== null &&
                  summary?.dso_dias !== undefined ? (
                     <MetricUnit>dias</MetricUnit>
                  ) : null}
               </MetricValueRow>
               <MetricDenominator>
                  Prazo médio da emissão do boleto ao pagamento — não da venda.
               </MetricDenominator>
            </MetricBlock>
         </MetricGrid>
      </>
   );
}

CreditQuality.propTypes = {
   summary: summaryShape.isRequired,
   dateBasis: PropTypes.string.isRequired,
};
